import React from "react";
import CardMedia from "@mui/material/CardMedia";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "../../../video-lab/dtv-text";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import { KeyValuePair } from "@directv-digital/dds.services.channel-lineup-provider";
import { ChannelCardV2 } from "lib/helper/channel-interactive-tool-v2";
import { IChnlInteractiveThemeUI } from "lib/helper/channel-interactive-tool-theme";
import ZipcodeModule from "../../../video-lab/zipcode-module";
import { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";
import { County } from "@directv-digital/dds.apis.county-lookup";
import ChannelIcon from "./ChannelIcon";
import ShowMoreButton from "./ShowMoreButton";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import {
  categorySectionContainerStyles,
  getCategoryHeaderStyles,
  getCategoryTooltipStyles,
  channelIconsContainerStyles,
  getMyHomeTeamCtaStyles,
  zipcodeModuleContainerStyles
} from "./styles";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";

interface CategorySectionProps {
  category: KeyValuePair;
  categoryIndex: number;
  channels: ChannelCardV2[];
  theme?: IChnlInteractiveThemeUI;
  genreEnabled?: boolean;
  openChannelModal?: boolean;
  tooltips?: any;
  zipCodeModule?: any;
  myHomeTeamModalCta?: { label: string; targeturl: string };
  showMoreKeys: Record<string, boolean>;
  onChannelClick: (channel: ChannelCardV2) => void;
  onShowMore: (categoryKey: string) => void;
  calcShowMore: (element: HTMLElement | null) => void;
  // Zipcode module handler props (for MYHOMETEAM inline module)
  onZipcodeSearch?: (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => void;
  onLocalRsnApiError?: (error?: string) => void;
  onLocalRsnChnlsConfirmed?: (channels: LocalRsnApiResponse | null, dma?: string, nielsenDma?: string) => void;
  onCacheConfirmed?: (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => void;
  localRsnApiOptions?: { satellite?: boolean; stream?: boolean };
  zipcodeInitData?: {
    zipcode?: string;
    counties?: County[] | null;
    county?: County | null;
    rsnFee?: string;
    localRsnChnls?: LocalRsnApiResponse | null;
  } | null;
}

const CategorySection: React.FC<CategorySectionProps> = ({
  category,
  categoryIndex,
  channels,
  theme,
  genreEnabled = false,
  openChannelModal = true,
  tooltips,
  zipCodeModule,
  myHomeTeamModalCta,
  showMoreKeys,
  onChannelClick,
  onShowMore,
  calcShowMore,
  onZipcodeSearch,
  onLocalRsnApiError,
  onLocalRsnChnlsConfirmed,
  onCacheConfirmed,
  localRsnApiOptions,
  zipcodeInitData
}) => {
  try {
    if (!channels.length) {
      return null;
    }

    const handleChannelClick = (channel: ChannelCardV2) => {
      // Use new hasIncludedPackages flag or check package arrays
      const hasPackages = channel.hasIncludedPackages || (channel.signaturePackages?.length || 0) > 0 || (channel.genrePackages?.length || 0) > 0;

      if (hasPackages) {
        onChannelClick(channel);
      }
    };

    const capitalizeCategory = (value: string) => value
      ?.split(" ")
      .map((s: string) => s.charAt(0).toUpperCase() + s.substring(1))
      .join(" ");

    const isClickable = (channel: ChannelCardV2) => {
      const hasPackages = channel.hasIncludedPackages || (channel.signaturePackages?.length || 0) > 0 || (channel.genrePackages?.length || 0) > 0;
      return hasPackages && openChannelModal;
    };

    return (
      <DtvBox key={category.key} sx={categorySectionContainerStyles}>
        <fieldset style={{ border: "none" }}>
          {/* Category Header */}
          <legend>
            <DtvText
              content={capitalizeCategory(category.value)}
              key={`category-${categoryIndex}`}
              sx={getCategoryHeaderStyles(theme)}
              variant="body2medium"
              tooltip={
                genreEnabled ?
                  {
                      content: tooltips?.genreTooltips?.[category.key]?.content || null,
                      mode: theme?.category?.mode === "light" ? "light" : "dark",
                      sx: getCategoryTooltipStyles(theme)
                    } :
                  undefined
              }
            />
          </legend>

          {/* Zipcode Module (for MYHOMETEAM category) */}
          {category.key === "ATT_TV_MYHOMETEAM" && (
            <DtvBox sx={zipcodeModuleContainerStyles}>
              <ZipcodeModule
                ciConfig={zipCodeModule}
                id="chlup-zipcode-module-myhometeam"
                initData={zipcodeInitData}
                isConditional={true}
                localRsnApiOptions={localRsnApiOptions}
                mode={theme?.zipCodeModuleMode}
                position="left"
                size="small"
                onCacheConfirmed={onCacheConfirmed}
                onLocalRsnChnlsApiError={onLocalRsnApiError}
                onLocalRsnChnlsConfirmed={onLocalRsnChnlsConfirmed}
                onSearchButtonClick={onZipcodeSearch}
              />
            </DtvBox>
          )}

          {/* Channel Icons */}
          <DtvBox className="chnlCtg-icons" id={`chnlCtg-icons-${category.key}`} sx={channelIconsContainerStyles(showMoreKeys, category.key)}>
            {channels.map((channel, index) => (
              <ChannelIcon
                category={category}
                channel={channel}
                genreEnabled={genreEnabled}
                index={index}
                isClickable={isClickable(channel)}
                key={`${channel.id}-${index}`}
                theme={theme}
                onClick={handleChannelClick}
              />
            ))}
          </DtvBox>

          {/* Show More/Less Button */}
          <ShowMoreButton
            categoryIndex={categoryIndex}
            categoryKey={category.key}
            isExpanded={!!showMoreKeys?.[category.key]}
            theme={theme}
            onLoad={calcShowMore}
            onToggle={onShowMore}
          />
        </fieldset>

        {/* My Home Team Modal CTA */}
        {category.key === "ATT_TV_MYHOMETEAM" && myHomeTeamModalCta && (
          <DtvButton
            ariaLabel={myHomeTeamModalCta.label}
            googleAnalyticsData={getAnalyticsAttrs(myHomeTeamModalCta.label?.toLowerCase(), 'panel_channel lineup', myHomeTeamModalCta.targeturl)}
            label={myHomeTeamModalCta.label}
            modalData={{ url: myHomeTeamModalCta.targeturl }}
            modalType="iframe"
            mode="light"
            sx={getMyHomeTeamCtaStyles(theme)}
            variant="uxtext"
            endIcon={
              <CardMedia
                alt={myHomeTeamModalCta.label}
                component="img"
                loading="lazy"
                src="https://www.directv.com/dtvassets/global/icons/2022/arrow-right_dk-bg.svg"
                sx={{ height: "16px" }}
              />
            }
          />
        )}
      </DtvBox>
    );
  } catch (error) {
    console.error("Error in CategorySection >> ", error);
    return <ErrorDisplay component="Category Section" error={error} />;
  }
};

export default React.memo(CategorySection);
